import axios from 'axios';
import { getJobApiCall, postJobApiCall, patchJobApiCall } from '../../utils/jobRequest';
import { handle403Logout } from '../../utils/request';
import {
  SET_CHAT_CONVERSATION,
  SET_CHAT_MESSAGES,
  UPSERT_CHAT_MESSAGE,
  SET_CHAT_PRESENCE,
  APPLY_CHAT_READ_RECEIPT,
  RESET_CHAT,
  SET_CHAT_INBOX,
  UPSERT_INBOX_ITEM,
  PREPEND_CHAT_MESSAGES,
  APPEND_CHAT_MESSAGES,
} from '../Types/types';

// ─── Plain action creators ─────────────────────────────────────────────────────

export const setChatConversation = (data) => ({ type: SET_CHAT_CONVERSATION, payload: data });
export const setChatMessages = (messages) => ({ type: SET_CHAT_MESSAGES, payload: messages });
export const upsertChatMessage = (message) => ({ type: UPSERT_CHAT_MESSAGE, payload: message });
export const setInboxItems = (items) => ({ type: SET_CHAT_INBOX, payload: items });
export const upsertInboxItem = (item) => ({ type: UPSERT_INBOX_ITEM, payload: item });
export const prependChatMessages = (messages) => ({ type: PREPEND_CHAT_MESSAGES, payload: messages });
export const appendChatMessages = (messages) => ({ type: APPEND_CHAT_MESSAGES, payload: messages });

export const setChatPresence = (recruiterOnline, candidateOnline) => ({
  type: SET_CHAT_PRESENCE,
  payload: { recruiter_online: recruiterOnline, candidate_online: candidateOnline },
});

export const applyReadReceipt = (senderType, lastReadMessageId) => ({
  type: APPLY_CHAT_READ_RECEIPT,
  payload: { sender_type: senderType, last_read_message_id: lastReadMessageId },
});

export const resetChat = () => ({ type: RESET_CHAT });

// ─── Candidate-side axios helper (x-api-key auth) ─────────────────────────────

function candidateChatCall(method, path, body) {
  return axios({
    method,
    url: `${process.env.REACT_APP_AI_API_KEY}${path}`,
    headers: {
      'x-api-key': process.env.REACT_APP_CANDIDATE_CHAT_API_KEY || '',
      'Content-Type': 'application/json',
    },
    data: body,
  });
}

// ─── Recruiter thunks (Bearer JWT via existing jobRequest utils) ───────────────

export const loadRecruiterConversation = (jcId) => async (dispatch) => {
  try {
    const [convRes, msgRes] = await Promise.all([
      getJobApiCall(`job-candidates/${jcId}/conversation`),
      getJobApiCall(`job-candidates/${jcId}/messages?limit=50&order_by=desc`),
    ]);
    const convData = convRes?.status === 200 ? convRes.data.data : null;
    if (convData) {
      dispatch(setChatConversation(convData));
    }
    if (msgRes?.status === 200) {
      // API returns desc (newest first) → reverse so oldest is at index 0 for display
      dispatch(setChatMessages(msgRes.data.data?.messages || []));
    }
    return { success: true, conversation: convData };
  } catch (error) {
    handle403Logout(error);
    return { success: false, conversation: null };
  }
};

export const loadOlderRecruiterMessages = (jcId, cursorId) => async (dispatch) => {
  try {
    const res = await getJobApiCall(
      `job-candidates/${jcId}/messages?limit=50&order_by=desc&cursor=${cursorId}&direction=before`
    );
    if (res?.status === 200) {
      const msgs = res.data.data?.messages || [];
      if (msgs.length > 0) {
        dispatch(appendChatMessages(msgs));
        return { success: true, hasMore: true };
      }
      return { success: true, hasMore: false };
    }
    return { success: false, hasMore: false };
  } catch (error) {
    handle403Logout(error);
    return { success: false, hasMore: false };
  }
};

export const refreshRecruiterConversation = (jcId) => async (dispatch) => {
  try {
    const res = await getJobApiCall(`job-candidates/${jcId}/conversation`);
    if (res?.status === 200) dispatch(setChatConversation(res.data.data));
  } catch (_) {}
};

export const sendRecruiterMessage = (jcId, bodyText, channel) => async (dispatch) => {
  try {
    const res = await postJobApiCall(`job-candidates/${jcId}/messages`, {
      body_text: bodyText,
      channel,
    });
    if (res?.status === 200 || res?.status === 201) {
      if (res.data?.data) dispatch(upsertChatMessage(res.data.data));
      return { success: true, data: res.data?.data };
    }
    // postJobApiCall returns the axios error object (not throws) for 400
    const errDetail = res?.response?.data?.detail;
    if (errDetail) return { success: false, errorDetail: errDetail };
    return { success: false };
  } catch (error) {
    handle403Logout(error);
    return { success: false };
  }
};

export const sendRecruiterMessageWithAttachment = (jcId, file, caption) => async (dispatch) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    if (caption) formData.append('body_text', caption);
    const res = await axios.post(
      `${process.env.REACT_APP_AI_API_KEY}job-candidates/${jcId}/messages/with-attachment`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
        },
      },
    );
    if (res?.status === 200 || res?.status === 201) {
      if (res.data?.data) dispatch(upsertChatMessage(res.data.data));
      return { success: true, data: res.data?.data };
    }
    return { success: false };
  } catch (error) {
    handle403Logout(error);
    return { success: false };
  }
};

export const markRecruiterRead = (jcId, lastReadMessageId) => async () => {
  try {
    await patchJobApiCall(`job-candidates/${jcId}/read`, {
      last_read_message_id: lastReadMessageId,
    });
    return { success: true };
  } catch (_) {
    return { success: false };
  }
};

export const loadRecruiterInbox = (search = '') => async (dispatch) => {
  try {
    let path = 'job-candidates/conversations?page=1&page_size=50';
    if (search) path += `&search=${encodeURIComponent(search)}`;
    const res = await getJobApiCall(path);
    if (res?.status === 200) {
      dispatch(setInboxItems(res.data.data?.items || []));
    }
    return { success: true, totalUnread: res?.data?.data?.total_unread || 0 };
  } catch (error) {
    handle403Logout(error);
    return { success: false };
  }
};

export const getRecruiterChatLink = (jcId) => async () => {
  try {
    const res = await getJobApiCall(`job-candidates/${jcId}/chat-link`);
    if (res?.status === 200) return { success: true, data: res.data?.data };
    return { success: false };
  } catch (_) {
    return { success: false };
  }
};

// ─── Candidate thunks (x-api-key) ─────────────────────────────────────────────

export const loadOlderCandidateMessages = (jcId, cursorId) => async (dispatch) => {
  try {
    const res = await candidateChatCall(
      'get',
      `candidate-chat/${jcId}/messages?limit=50&order_by=desc&cursor=${cursorId}&direction=before`
    );
    if (res?.status === 200) {
      const msgs = res.data.data?.messages || [];
      if (msgs.length > 0) {
        dispatch(appendChatMessages(msgs));
        return { success: true, hasMore: true };
      }
      return { success: true, hasMore: false };
    }
    return { success: false, hasMore: false };
  } catch (_) {
    return { success: false, hasMore: false };
  }
};

export const loadCandidateConversation = (jcId) => async (dispatch) => {
  try {
    const [convRes, msgRes] = await Promise.all([
      candidateChatCall('get', `candidate-chat/${jcId}/conversation`),
      candidateChatCall('get', `candidate-chat/${jcId}/messages?limit=50&order_by=desc`),
    ]);
    if (convRes?.status === 200) {
      dispatch(setChatConversation(convRes.data.data));
    }
    if (msgRes?.status === 200) {
      dispatch(setChatMessages(msgRes.data.data?.messages || []));
    }
    return { success: true };
  } catch (_) {
    return { success: false };
  }
};

export const searchCandidateMessages = (jcId, search) => async () => {
  try {
    const res = await candidateChatCall(
      'get',
      `candidate-chat/${jcId}/messages?limit=50&search=${encodeURIComponent(search)}`
    );
    if (res?.status === 200) {
      return { success: true, messages: res.data.data?.messages || [] };
    }
    return { success: false, messages: [] };
  } catch (_) {
    return { success: false, messages: [] };
  }
};

export const refreshCandidateConversation = (jcId) => async (dispatch) => {
  try {
    const res = await candidateChatCall('get', `candidate-chat/${jcId}/conversation`);
    if (res?.status === 200) dispatch(setChatConversation(res.data.data));
  } catch (_) {}
};

export const sendCandidateMessage = (jcId, bodyText, channel) => async (dispatch) => {
  try {
    const res = await candidateChatCall('post', `candidate-chat/${jcId}/messages`, {
      body_text: bodyText,
      channel,
    });
    if (res?.status === 200 || res?.status === 201) {
      if (res.data?.data) dispatch(upsertChatMessage(res.data.data));
      return { success: true, data: res.data?.data };
    }
    return { success: false };
  } catch (_) {
    return { success: false };
  }
};

export const markCandidateRead = (jcId, lastReadMessageId) => async () => {
  try {
    await candidateChatCall('patch', `candidate-chat/${jcId}/read`, {
      last_read_message_id: lastReadMessageId,
    });
    return { success: true };
  } catch (_) {
    return { success: false };
  }
};
